import { PropsWithChildren, ReactNode, useState } from 'react';
import { IonSpinner, IonThumbnail, isPlatform } from '@ionic/react';
import { useIsNative } from '../support/hooks/useIsNative.tsx';
import { createUseStyles } from 'react-jss';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { AnchorButton } from '../components/button/AnchorButton.tsx';
import { DocumentIcon, ImageIcon, PlayIcon } from '../components/icons/Icons.tsx';

const useStyles = createUseStyles({
    thumbnail: {
        width: 95,
        '--size': '60px',
        minHeight: 58, // needed when uploading
        border: '1px solid var(--ion-color-step-200)',
        backgroundColor: 'var(--ion-color-light)',
        position: 'relative'
    },

    thumbnailMask: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'white',
        opacity: 0.8,
        '& ion-spinner': {
            width: '70%',
            height: '90%'
        }
    },

    innerWrapper: {
        padding: 5,
        paddingBottom: 0,
        fontSize: 14,
        height: 52,
        lineHeight: 1,
        overflow: 'hidden',
        wordBreak: 'break-all',
        display: 'inline-block',
        '& ion-icon': {
            color: 'var(--ion-color-dark)',
            verticalAlign: 'top'
        }
    },
});

interface INoteAttachmentProps {
    url: string;
    fileName: string;
    isUploading?: boolean;
    contentType?: string;
}

const attachmentsDirPath = 'attachments';

const NoteAttachmentButton: React.FC<PropsWithChildren<INoteAttachmentProps>> = ({ url, fileName, isUploading, children }) => {
    const classes = useStyles();
    const [downloading, setDownloading] = useState<boolean>(false);
    const isNative = useIsNative();

    return !isUploading ? isNative ? (
        // native device
        downloading ? (
            <>
                {children}
                <div className={classes.thumbnailMask}>
                    <IonSpinner name="dots" color="primary" />
                </div>
            </>
        ) : (
            <AnchorButton
                newWindow
                noDecoration
                onClick={async () => {
                    try {
                        // create unique file path (directory) for each file
                        let localFilePath: string | undefined;
                        const base64S3Path = btoa(encodeURIComponent(url));
                        const filePath = `${attachmentsDirPath}/${base64S3Path}/${fileName}`;

                        try {
                            // has the file already been downloaded?
                            const { uri } = await Filesystem.stat({
                                path: filePath,
                                directory: Directory.Cache
                            });

                            localFilePath = uri;
                        } catch (err: any) {
                            // file hasn't been downloaded, download it and show an indicator in case slow
                            setDownloading(true);

                            // android needs us to create the directories first
                            if (isPlatform('android')) {
                                await Filesystem.mkdir({
                                    recursive: true,
                                    directory: Directory.Cache,
                                    path: `${attachmentsDirPath}/${base64S3Path}`
                                });
                            }

                            const { path } = await Filesystem.downloadFile({
                                url,
                                path: filePath,
                                directory: Directory.Cache
                            });

                            setDownloading(false);
                            if (path) {
                                localFilePath = path;
                            } else {
                                throw new Error(`Filesystem.downloadFile failed? ${filePath} was not downloaded successfully`);
                            }
                        }

                        // open it in the viewer
                        await FileOpener.open({
                            openWithDefault: true,
                            filePath: localFilePath
                        });
                    } catch (err: any) {
                        // '8' FILE_NOT_SUPPORTED
                        // https://github.com/capacitor-community/file-opener?tab=readme-ov-file#api
                        if (err.code === '8') {
                            const link = document.createElement('a');
                            link.href = url;
                            link.target = '_blank';
                            link.click();
                        }

                        // log to Sentry so we can see if we need to deal with any specific errors
                        throw err;
                    }
                }}
            >
                {children}
            </AnchorButton>
        )
    ) : (
        // browser
        // PDF / Image - on web, opens in another tab, no issue
        // Image - on android, opens in separate chrome app, no issue
        // PDF - on android, opens  in separate chrome app, downloads, then I have to open it, finally picking what to open it with
        //    by then, back is hosed (maybe)
        <AnchorButton newWindow noDecoration href={url}>{children}</AnchorButton>

    ) : <>{children}</>;
};

export const NoteAttachment: React.FC<INoteAttachmentProps> = (props) => {
    const classes = useStyles();

    const {fileName, url, isUploading = false, contentType} = props;

    const getCompForContentType = () => {
        const isImage: boolean = contentType?.startsWith('image/') || false;
        const isVideo: boolean = contentType?.startsWith('video/') || false;

        return isImage && !isUploading ? (
            <IonThumbnail className={classes.thumbnail}>
                <img src={url} alt="attachment" className={classes.thumbnail} />
            </IonThumbnail>
        ) : (
            <div className={classes.thumbnail}>
                <div className={classes.innerWrapper}>
                    {isImage ? <ImageIcon /> : isVideo ? <PlayIcon /> : <DocumentIcon />}
                    &nbsp;
                    {fileName.substring(0, fileName.lastIndexOf('.')) || fileName}
                </div>
                {isUploading && (
                    <div className={classes.thumbnailMask}>
                        <IonSpinner name="dots" color="primary" />
                    </div>
                )}
            </div>
        );
    };

    const dataProps = {
        'data-type': isUploading ?  'uploading-file': 'attachment-button'
    };

    return (
        <div {...dataProps} style={{position: 'relative'}}>
            <NoteAttachmentButton {...props}>
                {getCompForContentType()}
            </NoteAttachmentButton>
        </div>

    );
};
